import { useUser } from "@/contexts/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useChatState } from "@/components/chat/state/useChatState";
import ChatSidebar from "@/components/chat/ChatSidebar";
import ChatMainWindow from "@/components/chat/ChatMainWindow";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Users } from "lucide-react";
import { useEffect, useRef } from "react";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";

export default function ChatPage() {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId?: string }>();
  const {
    chatPreviews,
    selectedChatId,
    loading,
    recipient,
    messages,
    handleSelectChat,
    handleSendMessage,
    handleProfilePicChange,
    handleCreateGroupChat,
    handleCreateNewChat,
  } = useChatState(user, location);

  // Track last values to detect actual changes and prevent loops
  const lastChatIdRef = useRef<string | undefined>(chatId);
  const lastSelectedChatIdRef = useRef<string | undefined>(selectedChatId);
  const isNavigatingRef = useRef(false);
  
  // Sync URL when chat is selected from sidebar
  useEffect(() => {
    if (loading || chatPreviews.length === 0) return;
    
    // Only sync if selectedChatId changed and doesn't match URL
    // And we're not currently navigating (to prevent loop)
    if (
      selectedChatId && 
      selectedChatId !== lastSelectedChatIdRef.current &&
      selectedChatId !== chatId &&
      !isNavigatingRef.current
    ) {
      const chatExists = chatPreviews.some((chat) => chat.id === selectedChatId);
      if (chatExists) {
        isNavigatingRef.current = true;
        lastSelectedChatIdRef.current = selectedChatId;
        lastChatIdRef.current = selectedChatId;
        navigate(`/chat/${selectedChatId}`, { replace: true });
        // Reset flag after navigation
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    } else if (selectedChatId === chatId) {
      // They're in sync, update refs
      lastSelectedChatIdRef.current = selectedChatId;
      lastChatIdRef.current = chatId;
      isNavigatingRef.current = false;
    }
  }, [selectedChatId, chatId, navigate, loading, chatPreviews]);

  // Handle URL change - select chat from URL (including initial load)
  useEffect(() => {
    // Wait for previews to load and not currently navigating
    if (loading || chatPreviews.length === 0 || isNavigatingRef.current) {
      return;
    }
    
    // Handle if URL has chatId
    if (chatId) {
      const chatExists = chatPreviews.some((chat) => chat.id === chatId);
      
      if (chatExists) {
        // Select chat if it doesn't match selectedChatId
        // This handles both initial load and URL changes
        if (chatId !== selectedChatId) {
          lastChatIdRef.current = chatId;
          lastSelectedChatIdRef.current = chatId;
          handleSelectChat(chatId);
        } else {
          // They're in sync, just update refs
          lastChatIdRef.current = chatId;
          lastSelectedChatIdRef.current = selectedChatId;
        }
      } else {
        // Chat not found in previews
        lastChatIdRef.current = undefined;
        navigate("/chat", { replace: true });
      }
    }
  }, [chatId, loading, chatPreviews, selectedChatId, handleSelectChat, navigate]);

  // Find the selected chat to determine if it's a group
  const selectedChat = chatPreviews.find((chat) => chat.id === selectedChatId);
  const isGroupChat = selectedChat?.type === "group";

  const handleInviteMembers = () => {
    navigate("/team");
  };

  return (
    <div className="h-full w-full overflow-hidden flex flex-col">
      {/* Enhanced Messages Header - Sticky */}
      <div className="flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-card dark:to-muted/50 rounded-lg p-4 mb-4 border border-indigo-200 dark:border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-muted rounded-lg">
              <MessageCircle className="h-5 w-5 text-indigo-600 dark:text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-indigo-900 dark:text-foreground">Messages</h1>
              <p className="text-sm text-muted-foreground">Connect with your team</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-white/80 dark:bg-muted text-indigo-700 dark:text-foreground">
              <Users className="w-3 h-3 mr-1" />
              {chatPreviews.length} conversations
            </Badge>
          </div>
        </div>
      </div>

      <div className="flex flex-1 gap-0 min-h-0 bg-background rounded-lg overflow-hidden border shadow-sm">
        {/* Resizable chat layout - Desktop */}
        <ResizablePanelGroup direction="horizontal" className="hidden md:flex">
          {/* Chat list sidebar */}
          <ResizablePanel 
            defaultSize={33} 
            minSize={20} 
            maxSize={50}
            className="flex flex-col overflow-hidden"
          >
            <ChatSidebar
              loading={loading}
              chatPreviews={chatPreviews}
              selectedChatId={selectedChatId}
              onSelectChat={handleSelectChat}
              onCreateGroupChat={handleCreateGroupChat}
              onCreateNewChat={handleCreateNewChat}
            />
          </ResizablePanel>

          {/* Resizable handle */}
          <ResizableHandle withHandle />

          {/* Chat window */}
          <ResizablePanel 
            defaultSize={67} 
            minSize={50}
            className="flex flex-col overflow-hidden"
          >
            <ChatMainWindow
              loading={loading}
              selectedChatId={selectedChatId}
              recipient={recipient}
              messages={messages}
              currentUserId={user?.id || ""}
              onSendMessage={handleSendMessage}
              onProfilePicChange={handleProfilePicChange}
              chatType={isGroupChat ? "group" : "individual"}
              groupName={selectedChat?.groupName}
              groupMembers={selectedChat?.members}
              onInviteMembers={handleInviteMembers}
            />
          </ResizablePanel>
        </ResizablePanelGroup>

        {/* Mobile view - non-resizable */}
        <div className="w-full md:hidden flex flex-col overflow-hidden">
          {selectedChatId ? (
            <ChatMainWindow
              loading={loading}
              selectedChatId={selectedChatId}
              recipient={recipient}
              messages={messages}
              currentUserId={user?.id || ""}
              onSendMessage={handleSendMessage}
              onProfilePicChange={handleProfilePicChange}
              chatType={isGroupChat ? "group" : "individual"}
              groupName={selectedChat?.groupName}
              groupMembers={selectedChat?.members}
              onInviteMembers={handleInviteMembers}
            />
          ) : (
            <ChatSidebar
              loading={loading}
              chatPreviews={chatPreviews}
              selectedChatId={selectedChatId}
              onSelectChat={handleSelectChat}
              onCreateGroupChat={handleCreateGroupChat}
              onCreateNewChat={handleCreateNewChat}
            />
          )}
        </div>
      </div>
    </div>
  );
}
